
import { supabase } from '@/integrations/supabase/client';
import { socialService } from './socialService';

export const feedService = {
  async getFollowingFeed(userId: string, limit: number = 20) {
    const followingUsers = await socialService.getFollowing(userId);
    if (!followingUsers || followingUsers.length === 0) {
      return [];
    }
    const followingUserIds = followingUsers.map(f => f.following_id).filter((id): id is string => !!id);

    if (followingUserIds.length === 0) {
      return [];
    }
    
    const [posts, comments, podcastComments] = await Promise.all([
      supabase.from('user_posts').select(`*, user_profiles!inner(id, username, full_name, bio, avatar_url, avatar_background, total_xp, current_streak, longest_streak, current_league, league_position, achievements_unlocked, created_at, updated_at, last_active_date)`).in('user_id', followingUserIds).order('created_at', { ascending: false }).limit(limit),
      supabase.from('article_comments').select(`*, articles(*), user_profiles!inner(id, username, full_name, bio, avatar_url, avatar_background, total_xp, current_streak, longest_streak, current_league, league_position, achievements_unlocked, created_at, updated_at, last_active_date)`).in('user_id', followingUserIds).order('created_at', { ascending: false }).limit(limit),
      supabase.from('podcast_episode_comments').select(`*, podcast_episodes(*, podcast:podcast_sources(*)), profiles:user_id(id, username, full_name, avatar_url, avatar_background)`).in('user_id', followingUserIds).order('created_at', { ascending: false }).limit(limit)
    ]);

    if (posts.error) {
        console.error('Error fetching following posts:', posts.error);
    }
    if (comments.error) {
        console.error('Error fetching following comments:', comments.error);
    }
    if (podcastComments.error) {
        console.error('Error fetching following podcast comments:', podcastComments.error);
    }

    const allActivities = [
      ...(posts.data || []).map(post => ({
        ...post,
        type: 'post' as const,
        created_at: post.created_at
      })),
      ...(comments.data || []).map(comment => ({
        ...comment,
        type: 'comment' as const,
        created_at: comment.created_at
      })),
      ...(podcastComments.data || []).map(comment => ({
        ...comment,
        type: 'podcast_comment' as const,
        created_at: comment.created_at
      }))
    ];

    const sortedActivities = allActivities
      .sort((a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime())
      .slice(0, limit);

    return sortedActivities;
  },
  
  async getForYouFeed(limit: number = 20) {
    const [posts, comments, podcastComments] = await Promise.all([
      supabase.from('user_posts').select(`*, user_profiles!inner(id, username, full_name, bio, avatar_url, avatar_background, total_xp, current_streak, longest_streak, current_league, league_position, achievements_unlocked, created_at, updated_at, last_active_date)`).order('created_at', { ascending: false }).limit(limit),
      supabase.from('article_comments').select(`*, articles(*), user_profiles!inner(id, username, full_name, bio, avatar_url, avatar_background, total_xp, current_streak, longest_streak, current_league, league_position, achievements_unlocked, created_at, updated_at, last_active_date)`).order('created_at', { ascending: false }).limit(limit),
      supabase.from('podcast_episode_comments').select(`*, podcast_episodes(*, podcast:podcast_sources(*)), profiles:user_id(id, username, full_name, avatar_url, avatar_background)`).order('created_at', { ascending: false }).limit(limit)
    ]);
    
    if (posts.error) {
        console.error('Error fetching for you posts:', posts.error);
    }
    if (comments.error) {
        console.error('Error fetching for you comments:', comments.error);
    }
    if (podcastComments.error) {
        console.error('Error fetching for you podcast comments:', podcastComments.error);
    }

    const allActivities = [
      ...(posts.data || []).map(post => ({
        ...post,
        type: 'post' as const,
        created_at: post.created_at
      })),
      ...(comments.data || []).map(comment => ({
        ...comment,
        type: 'comment' as const,
        created_at: comment.created_at
      })),
      ...(podcastComments.data || []).map(comment => ({
        ...comment,
        type: 'podcast_comment' as const,
        created_at: comment.created_at
      }))
    ];

    const sortedActivities = allActivities
      .sort((a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime())
      .slice(0, limit);

    return sortedActivities;
  },

  async getTrendingTopics() {
    const { data: posts, error } = await supabase
      .from('user_posts')
      .select('content')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Error fetching posts for trends:', error);
      return [];
    }

    const hashtagCounts: Record<string, number> = {};
    if (posts) {
      posts.forEach(post => {
        const hashtags = post.content.match(/#\w+/g);
        if (hashtags) {
          hashtags.forEach(tag => {
            const cleanedTag = tag.substring(1).toLowerCase();
            hashtagCounts[cleanedTag] = (hashtagCounts[cleanedTag] || 0) + 1;
          });
        }
      });
    }

    const trendingTopics = Object.entries(hashtagCounts)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5)
      .map(([topic, count]) => ({ topic: `#${topic}`, count }));
      
    return trendingTopics;
  }
};
